import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import ModalForms from '../../Shared/Modals';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions, LeadFormActions } from '../../../Services/_actions';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import {
  assignedCampaignUserAtom,
  campaignLeadManagementAtom,
  userInformationAtom,
} from '../../../Services/_states/index.js';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import CommentBox from '../../Shared/CommentBox/index.tsx';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { set } from 'date-fns';
import CommonBadges from '../../Shared/Badges/index.jsx';
import { globalpermissions } from '../../../Services/Permission.js';
import { leadsActionSelector } from '../../../Services/_states/Leads/leadSelector.ts';

interface ManageActionModalProps {
  open: boolean;
  onClose: () => void;
  lead?: any;
}

const ManageActionModal: React.FC<ManageActionModalProps> = ({ open, onClose, lead }) => {
  const [campaignLeadManagementData] = useRecoilState(campaignLeadManagementAtom);
  const statusList =
    campaignLeadManagementData?.statusData?.[
    campaignLeadManagementData?.selectedForm?.leads_form_id
    ] ?? [];
  const setLeadActions = useSetRecoilState(leadsActionSelector);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const organisationId = loggedInUser?.profile?.organisation?.organisation_id;

  const constantActions = ConstantActions();
  const leadActions = LeadFormActions();
  const { showSnackBar } = useSnackBar();

  // Form states
  const [status, setStatus] = useState < any > (null);
  const [selectedUser, setSelectedUser] = useState < any[] > ([]);
  const [unassignSelectedUser, setUnassignSelectedUser] = useState < any[] > ([]);

  const [commentText, setCommentText] = useState < string > ('');
  const [commentList, setCommentList] = useState < any[] > ([]);
  // const [nextSubStatus, setNextSubStatus] = useState<any>(null);

  // Loading and data states
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [userList, setUserList] = useState([]);
  const [userList, setUserList] = useRecoilState(assignedCampaignUserAtom);
  const campaignId = campaignLeadManagementData?.selectedCampaign?.proposal_id;

  const [assignedUsers, setAssignedUsers] = useState < any[] > ([]);

  // Original values for comparison
  const [originalStatus, setOriginalStatus] = useState < any > (null);
  const [originalAssignedUsers, setOriginalAssignedUsers] = useState < any[] > ([]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (lead && open) {
      const currentStatus = lead?.last_status ?? null;
      const currentAssignedUsers =
        lead?.lead_assignment?.map((item) => ({
          ...item,
          id: Number(item?.lead_assigned_to),
        })) || [];
      setStatus(currentStatus);
      setOriginalStatus(currentStatus);
      setAssignedUsers(currentAssignedUsers);
      setOriginalAssignedUsers(currentAssignedUsers);
      setCommentList(lead?.internal_comments || []);
      setSelectedUser([]);
      setCommentText('');
      setUnassignSelectedUser([]);
    }
  }, [lead, open]);

  const getNextStatusByStatus = () => {
    const result: any = statusList?.find(
      (item: any) => item?.status_id === (status?.status_id ?? lead?.last_status?.status_id)
    );
    return result?.substatuses || [];
  };

  // Get user list
  const getUserMinimalList = async () => {
    if (!campaignId) {
      return;
    }
    if (userList[campaignId] && userList[campaignId] <= 0) {
      return;
    }

    try {
      if (organisationId) {
        const res: any = await constantActions.getUserMinimalList(organisationId, {
          campaign_id: campaignId ?? '',
        });
        // setUserList(res ?? []);
        setUserList((prev) => ({ ...prev, [campaignId]: res || [] }));
      }
    } catch (err) {
      showSnackBar(err?.toString() ?? 'Server error. Please try again.', 'error');
    }
  };

  useEffect(() => {
    if (lead && open) {
      getUserMinimalList();
    }
  }, [lead, open, organisationId]);

  // Check what has changed
  const getChanges = () => {
    const changes = {
      statusChanged: false,
      assignmentChanged: false,
      unassignmentChanged: false,
      commentAdded: false,
      nextSubStatusChanged: false,
    };

    // Check status change
    if (originalStatus?.status_id !== status?.status_id) {
      changes.statusChanged = true;
    }

    // Check assignment change
    if (selectedUser.length > 0) {
      changes.assignmentChanged = true;
    }

    // Check unassignment change
    if (unassignSelectedUser.length > 0) {
      changes.unassignmentChanged = true;
    }

    // Check comment
    if (commentList.some((c) => c?.isNewComment)) {
      changes.commentAdded = true;
    }

    // Check next sub-status
    // if (nextSubStatus && lead?.substatuses?.sub_status_name !== nextSubStatus?.name) {
    //   changes.nextSubStatusChanged = true;
    // }
    // if (nextSubStatus && lead?.substatuses?.sub_status_id !== nextSubStatus?.sub_status_id) {
    //   changes.nextSubStatusChanged = true;
    // }
    return changes;
  };

  // Individual API functions
  const updateStatus = async () => {
    const data = {
      id: lead?.id,
      status: status?.status_name || null,
      status_id: status?.status_id || null,
      type_of_status: "default"
      // triggers: status?.triggers || [],
    };

    const res: any = await leadActions.updateLeadStatusDate(data);
    if (res?.status !== 200) {
      throw new Error('Failed to update lead status');
    }
    return res;
  };

  const assignUsers = async () => {
    const leadIds = [lead?.lead_id];
    const payload = {
      lead_ids: leadIds ?? [],
      assigned_to_user_ids: selectedUser.map((user: { id: number | string }) => user.id),
      campaign_id: lead?.campaign_id || campaignLeadManagementData?.selectedCampaign?.proposal_id,
    };

    const res: any = await leadActions.leadAssignment(payload);
    if (res?.status !== 200) {
      throw new Error(
        `Failed to assign users: ${res?.data?.data?.general_error?.message || 'Unknown error'}`
      );
    }
    return res;
  };

  const unassignUsers = async () => {
    try {
      const payload = {
        lead_ids: [lead?.lead_id ?? ''],
        unassign_from_user_ids: unassignSelectedUser?.map(
          (user: { id: number | string }) => user.id
        ),
        campaign_id: lead?.campaign_id || campaignLeadManagementData?.selectedCampaign?.proposal_id,
      };
      const res: { status: number | null; data: any | null } =
        await leadActions.leadUnAssignment(payload);
      if (res?.status !== 200) {
        throw new Error(
          `Failed to unassign users: ${res?.data?.data?.general_error?.message || 'Unknown error'}`
        );
      } else {
        return res;
      }
    } catch (err) {
      showSnackBar(err?.toString() ?? 'Server error. Please try again.', 'error');
    }
  };

  const addComment = async (e) => {
    try {
      const response: any = await leadActions.postCommentById(
        { comment: e, id: lead?.id },
        lead?.id
      );
      if (response?.status !== 200) {
        showSnackBar('Failed to add comment', 'error');
      } else {
        showSnackBar('Comment added successfully', 'success');
      }
    } catch (error) {
      showSnackBar(error?.toString() ?? 'Server error. Please try again.', 'error');
    }
  };

  // const updateNextSubStatus = async () => {
  //   const data = {
  //     id: lead?.id,
  //     sub_status_name: nextSubStatus?.name,
  //     sub_status_id: nextSubStatus?.sub_status_id,
  //     // triggers: nextSubStatus?.triggers || [],
  //     status_id: status?.status_id || '',
  //     status_name: status?.status_name || '',
  //     type_of_status:"default"
  //   };
  //   const res: any = await leadActions.updateLeadStatusDate(data);
  //   if (res?.status !== 200) {
  //     throw new Error('Failed to update next status');
  //   }
  //   return res;
  // };

  // Update table state after successful operations
  const updateTableState = (changes: any, results: any) => {
    let updatedRow = { ...lead };

    // Update status
    if (changes.statusChanged && results.statusResult) {
      updatedRow.last_status = { ...lead?.last_status, ...status };
    }

    // Update assignments
    if ((changes?.assignmentChanged && results?.assignmentResult) || (changes?.unassignmentChanged && results?.unassignmentResult)) {
      const unassignedUserIdsSet = new Set(
        (results?.unassignmentResult?.data?.data?.unassigned_from_users ?? []).map(String)
      );
      const assignedUsers = results?.assignmentResult?.data?.data?.assigned_users ?? [];
      const newUsers = assignedUsers?.map((item) => ({
        ...item,
        lead_assigned_to: String(item?.user_id ?? ''),
        id: String(item?.user_id ?? ''),
      }));
      const result = newUsers?.filter((item) => !unassignedUserIdsSet.has(item?.id));
      updatedRow.lead_assignment = [...result];
    }

    if (changes?.commentAdded) {
      updatedRow.internal_comments = commentList;
      if (commentList.length > 0) {
        updatedRow.last_machadalo_comment = {
          comment: commentList[commentList.length - 1]?.comment ?? '',
          created_by: loggedInUser?.username ?? 'You',
          created_at: new Date().toISOString(),
        };
      }
    }
    setLeadActions({ type: "update", payload: { ...lead, ...updatedRow } })
    // setTableState((prevState) => {
    //   const updatedCache = Object.entries(prevState.cache).reduce((acc, [key, rows]) => {
    //     const updatedRows = rows.map((row) => {
    //       if (row.id === lead?.id) {
    //         let updatedRow = { ...row };

    //         // Update status
    //         if (changes.statusChanged && results.statusResult) {
    //           updatedRow.last_status = { ...row?.last_status, ...status };
    //         }

    //         // Update assignments
    //         if (
    //           (changes?.assignmentChanged && results?.assignmentResult) ||
    //           (changes?.unassignmentChanged && results?.unassignmentResult)
    //         ) {
    //           const unassignedUserIdsSet = new Set(
    //             (results?.unassignmentResult?.data?.data?.unassigned_from_users ?? []).map(String)
    //           );
    //           const assignedUsers = results?.assignmentResult?.data?.data?.assigned_users ?? [];
    //           const newUsers = assignedUsers?.map((item) => ({
    //             ...item,
    //             lead_assigned_to: String(item?.user_id ?? ''),
    //             id: String(item?.user_id ?? ''),
    //           }));
    //           const result = newUsers?.filter((item) => !unassignedUserIdsSet.has(item?.id));
    //           updatedRow.lead_assignment = [...result];
    //           // const newAssignedUsers = results.assignmentResult?.data?.data?.assigned_users ?? [];
    //           // updatedRow.lead_assignment = [...assignedUsers, ...newAssignedUsers];
    //         }

    //         // Update comments
    //         if (changes.commentAdded) {
    //           updatedRow.internal_comments = commentList;
    //           if (commentList.length > 0) {
    //             updatedRow.last_machadalo_comment = {
    //               comment: commentList[commentList.length - 1]?.comment ?? '',
    //               created_by: loggedInUser?.username ?? 'You',
    //               created_at: new Date().toISOString(),
    //             };
    //           }
    //           // row.internal_comments
    //           // ? [
    //           //   ...row.internal_comments,
    //           //   { comment: commentText, updated_at: new Date().toISOString() },
    //           // ]
    //           // : [{ comment: commentText, updated_at: new Date().toISOString() }];
    //         }
    //         // if (changes.nextSubStatusChanged && results.nextSubStatusResult) {
    //         //   updatedRow.substatuses = {
    //         //     ...updatedRow.substatuses,
    //         //     sub_status_name: nextSubStatus?.name,
    //         //     sub_status_id: nextSubStatus?.sub_status_id ?? "",
    //         //     status_id: status?.status_id,
    //         //     status_name: status?.status_name,
    //         //     // triggers: nextSubStatus?.triggers || [],
    //         //   };
    //         // }
    //         return updatedRow;
    //       }
    //       return row;
    //     });
    //     acc[key] = updatedRows;
    //     return acc;
    //   }, {});
    //   return { ...prevState, cache: updatedCache };
    // });
  };

  // Main submit handler
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const changes = getChanges();

      // Check if any changes were made
      if (
        !changes.statusChanged &&
        !changes.assignmentChanged &&
        !changes.unassignmentChanged &&
        !changes.commentAdded &&
        !changes?.nextSubStatusChanged
      ) {
        showSnackBar('No changes detected', 'info');
        setIsSubmitting(false);
        return;
      }

      const results: any = {};
      const operations: Promise<any>[] = [];
      const operationNames: string[] = [];

      // Execute APIs based on changes
      if (changes?.statusChanged) {
        operations.push(updateStatus());
        operationNames.push('status update');
      }

      if (changes?.assignmentChanged) {
        operations.push(assignUsers());
        operationNames.push('user assignment');
      }

      if (changes?.unassignmentChanged) {
        operations.push(unassignUsers());
        operationNames.push('user unassignment');
      }

      // if (changes?.nextSubStatusChanged) {
      //   operations.push(updateNextSubStatus());
      //   operationNames.push('next status update');
      // }
      // if (changes.commentAdded && commentText.trim()) {
      //   operations.push(addComment(commentText));
      //   operationNames.push('comment addition');
      // }
      if (changes?.commentAdded) {
        const newComments = commentList.filter((c) => c.isNewComment && c.comment?.trim());
        const commentOps = newComments.map((comment) => {
          operationNames.push('comment addition');
          return addComment(comment?.comment);
        });
        operations.push(...commentOps);
      }

      // Execute all operations
      const operationResults = await Promise.allSettled(operations);

      // Process results
      let successCount = 0;
      let errors: string[] = [];

      operationResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successCount++;
          // Store results for table state update
          if (operationNames[index] === 'status update') {
            results.statusResult = result.value;
          } else if (operationNames[index] === 'user assignment') {
            results.assignmentResult = result.value;
          } else if (operationNames[index] === 'user unassignment') {
            results.unassignmentResult = result.value;
          } else if (operationNames[index] === 'comment addition') {
            results.commentResult = result.value;
          } else if (operationNames[index] === 'next status update') {
            results.nextSubStatusResult = result.value;
          }
        } else {
          errors.push(`${operationNames[index]}: ${result.reason.message}`);
        }
      });

      // Update table state for successful operations
      if (successCount > 0) {
        updateTableState(changes, results);

        // Update local state
        if (results.commentResult) {
          setCommentList([
            ...commentList,
            {
              comment: commentText,
              created_at: new Date(),
              created_by: loggedInUser?.username ?? 'You',
            },
          ]);
          setCommentText('');
        }

        if (results?.assignmentResult || results?.unassignmentResult) {
          const unassignedUserIdsSet = new Set(
            (results?.unassignmentResult?.data?.data?.unassigned_from_users ?? []).map(String)
          );
          const assignedUsers = results?.assignmentResult?.data?.data?.assigned_users ?? [];
          const newUsers = assignedUsers?.map((item) => ({
            ...item,
            id: String(item?.user_id ?? ''),
          }));
          const result = newUsers?.filter((item) => !unassignedUserIdsSet.has(item?.id));
          setAssignedUsers(result);
          setSelectedUser([]);
          setUnassignSelectedUser([]);
        }
      }

      // Show appropriate messages
      if (successCount === operations?.length) {
        showSnackBar(`All changes saved successfully!`, 'success');
        // Auto-close modal after successful submission
        setTimeout(() => {
          onClose();
        }, 1500);
      } else if (successCount > 0) {
        showSnackBar(
          `${successCount} out of ${operations?.length} operations completed successfully`,
          'warning'
        );
        if (errors.length > 0) {
          console.error('Partial failures:', errors);
        }
      } else {
        showSnackBar(`All operations failed: ${errors.join(', ')}`, 'error');
      }
    } catch (error) {
      showSnackBar(error?.toString() || 'Unexpected error occurred', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form has changes
  const hasChanges = () => {
    const changes = getChanges();
    return (
      changes.statusChanged ||
      changes.assignmentChanged ||
      changes.unassignmentChanged ||
      changes.commentAdded ||
      changes.nextSubStatusChanged
    );
  };

  const handleUnAssignedUser = (user) => {
    setUnassignSelectedUser([...unassignSelectedUser, user]);
    setAssignedUsers(assignedUsers?.filter((u) => u.id !== user.id));
  };

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="Manage Record"
      size="lg"
      modalBody={
        <Box>
          <Grid container spacing={2}>
            {/* Left Column: Status, Next Status, Assignment */}
            <Grid item xs={12} md={7}>
              {/* <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Update Status
                  </Typography>
                  <SearchableDropdown
                    size="small"
                    options={statusList}
                    value={status ?? null}
                    onChange={setStatus}
                    labelKey="status_name"
                    label="Select New Status"
                  />
                  {status && originalStatus?.status_id !== status?.status_id && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Status will be updated from "{originalStatus?.status_name}" to "
                      {status?.status_name}"
                    </Alert>
                  )}
                </CardContent>
              </Card> */}

              {/* <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Update Next Status
                  </Typography>
                  <SearchableDropdown
                    size="small"
                    options={getNextStatusByStatus()}
                    value={nextSubStatus ?? null}
                    onChange={(e) => setNextSubStatus(e)}
                    labelKey="name"
                    valueKey='sub_status_id'
                    label="Select Next Status"
                  />
                  {nextSubStatus && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Next status will be updated to "{nextSubStatus?.name}"
                    </Alert>
                  )}
                </CardContent>
              </Card> */}

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  {constantActions.userHasPermission([globalpermissions?.leadFormPage?.assignUnAssignUser]) && (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Assign & Unassign Users
                      </Typography>
                      {assignedUsers.length > 0 && (
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Currently Assigned:
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {assignedUsers.map((user, idx) => (
                              <Box key={idx}>
                                <CommonBadges
                                  color="success"
                                  value={user?.username || user?.name || 'Unknown User'}
                                  variant="outlined"
                                  onDelete={() => handleUnAssignedUser(user)}
                                  showDelete={true}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </>
                  )}
                  <SearchableDropdown
                    options={userList[campaignId] ?? []}
                    value={selectedUser}
                    labelKey="username"
                    valueKey="id"
                    onChange={setSelectedUser}
                    label="Add Users"
                    multiSelect={true}
                    getOptionDisabled={(option) =>
                      [...assignedUsers, ...unassignSelectedUser].some((u) => u?.id === option?.id)
                    }
                  />
                  {/* {selectedUser.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {selectedUser.length} user(s) will be assigned to this lead
                    </Alert>
                  )}
                  {unassignSelectedUser.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {unassignSelectedUser.length} user(s) will be unassigned to this lead
                    </Alert>
                  )} */}
                </CardContent>
              </Card>
              {/* <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Unassign Users
                  </Typography>
                  <SearchableDropdown
                    options={assignedUsers}
                    value={unassignSelectedUser}
                    labelKey="username"
                    valueKey="id"
                    onChange={setUnassignSelectedUser}
                    label="Add Users"
                    multiSelect={true}
                    getOptionDisabled={(option) =>
                      unassignSelectedUser.some(u => u?.id === option?.id)
                    }
                  />
                  {unassignSelectedUser.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {unassignSelectedUser.length} user(s) will be unassigned to this lead
                    </Alert>
                  )}
                </CardContent>
              </Card> */}
            </Grid>

            {/* Right Column: Comments */}
            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Comments
                  </Typography>
                  <CommentBox
                    commentList={commentList || []}
                    onSubmitComment={(e) => {
                      const newComment = {
                        comment: e,
                        created_at: new Date(),
                        created_by: loggedInUser?.username ?? 'You',
                        isNewComment: true, // flag for new comments
                      };
                      setCommentList([...commentList, newComment]);
                      setCommentText('');
                    }}
                  />
                  {commentText?.trim() && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      New comment will be added
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      }
      modalFooter={
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Box display="flex" gap={1}>
            <LoadingButton
              buttonName="Save All Changes"
              onClickButton={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSubmitting || !hasChanges()}
              size="medium"
              variant="contained"
            />
          </Box>
        </Box>
      }
    />
  );
};

export default ManageActionModal;
